/**
 * Playlist editor — port of app/templates/playlists/editor.html, same
 * API contract, same Mixpanel events ('Playlist Viewed', surface
 * 'editor'), so the dashboards read on uninterrupted.
 *
 * Reorder works two ways because input devices differ, not preferences:
 * HTML5 drag never fires on mobile browsers, so touch devices get
 * up/down buttons instead of a grip (CSS pointer:coarse decides).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { trackEvent } from '../../lib/analytics'
import { ApiError, api } from '../../lib/api'
import {
  type GameMeta, type PlaylistItem, type PlaylistMeta,
  clockFor, titleFor,
} from './shared'
import './playlists.css'

export default function PlaylistEditor() {
  const { playlistId } = useParams<{ playlistId: string }>()
  const navigate = useNavigate()
  const [playlist, setPlaylist] = useState<PlaylistMeta | null>(null)
  const [items, setItems] = useState<PlaylistItem[]>([])
  const [games, setGames] = useState<Record<string, GameMeta>>({})
  const [error, setError] = useState<string | null>(null)
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const dragId = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)
  const exportPoll = useRef<number | null>(null)

  // Explicit event (page views alone can't answer "who viewed this
  // playlist"), fired once per playlist on load SUCCESS — the Flask page
  // redirected unauthorized viewers before its script ran, so a 403
  // never counted as a view, and neither may this.
  const viewTracked = useRef<string | null>(null)

  const load = useCallback(() => {
    api<{ playlist: PlaylistMeta; items: PlaylistItem[]; games: Record<string, GameMeta> }>(
      `/api/v1/playlists/${playlistId}`)
      .then((d) => {
        if (playlistId && viewTracked.current !== playlistId) {
          viewTracked.current = playlistId
          trackEvent('Playlist Viewed', { playlist_id: playlistId, surface: 'editor' })
        }
        setPlaylist(d.playlist)
        setItems(d.items)
        setGames(d.games || {})
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
          void navigate('/clip-room')
          return
        }
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [playlistId, navigate])
  useEffect(load, [load])

  // MP4 export: kick off, then poll playlist.export until ready/failed.
  const exportStatus = playlist?.export?.status
  useEffect(() => {
    if (exportStatus === 'running' && exportPoll.current == null) {
      exportPoll.current = window.setInterval(() => {
        api<{ playlist: PlaylistMeta }>(`/api/v1/playlists/${playlistId}`)
          .then((d) => setPlaylist((p) => (p ? { ...p, export: d.playlist.export } : p)))
          .catch(() => { /* keep polling; transient errors resolve */ })
      }, 3000)
    }
    if (exportStatus !== 'running' && exportPoll.current != null) {
      window.clearInterval(exportPoll.current)
      exportPoll.current = null
    }
    return () => {
      if (exportPoll.current != null) {
        window.clearInterval(exportPoll.current)
        exportPoll.current = null
      }
    }
  }, [exportStatus, playlistId])

  const saveOrder = async (next: PlaylistItem[]) => {
    setItems(next)
    await api(`/api/v1/playlists/${playlistId}/items`, {
      method: 'PUT',
      body: JSON.stringify({ item_ids: next.map((i) => i.id) }),
    }).catch(() => load()) // a failed reorder re-syncs to the server's truth
  }

  const reorder = (from: number, to: number) => {
    const next = [...items]
    const [moved] = next.splice(from, 1)
    if (!moved) return
    next.splice(to, 0, moved)
    void saveOrder(next)
  }

  const moveItem = (itemId: string, dir: -1 | 1) => {
    const from = items.findIndex((i) => i.id === itemId)
    const to = from + dir
    if (from < 0 || to < 0 || to >= items.length) return
    reorder(from, to)
  }

  const drop = (targetId: string) => {
    const from = items.findIndex((i) => i.id === dragId.current)
    const to = items.findIndex((i) => i.id === targetId)
    setDragOver(null)
    if (from < 0 || to < 0 || from === to) return
    reorder(from, to)
  }

  const removeItem = async (itemId: string) => {
    try {
      await api(`/api/v1/playlists/${playlistId}/items/${itemId}`, { method: 'DELETE' })
      setItems((cur) => cur.filter((i) => i.id !== itemId))
    } catch {
      load() // a failed delete must not LOOK deleted — resync to the server
    }
  }

  const saveNote = async (itemId: string) => {
    try {
      await api(`/api/v1/playlists/${playlistId}/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify({ note: noteDraft || null }),
      })
      setItems((cur) => cur.map((i) => (i.id === itemId ? { ...i, note: noteDraft || null } : i)))
      setNoteFor(null)
    } catch {
      load()
    }
  }

  const rename = () => {
    if (!playlist) return
    const name = window.prompt('Rename playlist', playlist.name)
    if (name && name.trim()) {
      void api(`/api/v1/playlists/${playlistId}`, {
        method: 'PUT',
        body: JSON.stringify({ name: name.trim() }),
      }).then(() => setPlaylist({ ...playlist, name: name.trim() }))
    }
  }

  // A failed action must say so: these are void'd from onClick, so a
  // bare rejection would only ever reach the console.
  const failed = (what: string) => (e: unknown) => {
    window.alert(`${what} failed: ${e instanceof ApiError ? e.message : String(e)}`)
    load()
  }

  const toggleVisibility = async () => {
    if (!playlist) return
    const visibility = playlist.visibility === 'team' ? 'private' : 'team'
    try {
      await api(`/api/v1/playlists/${playlistId}`, {
        method: 'PUT',
        body: JSON.stringify({ visibility }),
      })
      setPlaylist({ ...playlist, visibility })
    } catch (e) {
      failed('Changing visibility')(e)
    }
  }

  const refreshSmartReel = async () => {
    setRefreshing(true)
    try {
      const d = await api<{ appended: number }>(
        `/api/v1/playlists/${playlistId}/refresh`, { method: 'POST' })
      if (d.appended > 0) load()
    } catch (e) {
      failed('Refresh')(e)
    } finally {
      setRefreshing(false)
    }
  }

  const deletePlaylist = async () => {
    if (!window.confirm('Delete this playlist? Clips themselves are not affected.')) return
    try {
      await api(`/api/v1/playlists/${playlistId}`, { method: 'DELETE' })
    } catch (e) {
      failed('Delete')(e)
      return
    }
    void navigate('/clip-room')
  }

  const exportReel = async () => {
    try {
      await api(`/api/v1/playlists/${playlistId}/export`, { method: 'POST' })
    } catch (e) {
      // 409 = already running: same optimistic UI either way.
      if (!(e instanceof ApiError && e.status === 409)) {
        failed('Export')(e)
        return
      }
    }
    setPlaylist((p) => (p ? { ...p, export: { status: 'running' } } : p))
  }

  if (error) return <div className="ple-page"><p className="error">{error}</p></div>
  if (!playlist) return <div className="ple-page"><p className="muted">Loading…</p></div>

  const secs = items.length * 10
  let meta = `${items.length} clip${items.length === 1 ? '' : 's'} · ~${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`
  if (playlist.source === 'rule' && playlist.rule) {
    const r = playlist.rule
    const parts: string[] = []
    if (r.moment_types?.length) parts.push(r.moment_types.map((t) => t.replace(/_/g, ' ')).join('/'))
    if (r.min_score) parts.push(`score>${r.min_score}`)
    if (r.zones?.length) parts.push(r.zones.join('+'))
    if (r.player_jersey != null) parts.push(`#${r.player_jersey}`)
    meta += ` · auto: ${parts.join(', ') || 'all moments'}`
  }
  const ex = playlist.export ?? {}

  return (
    <div className="ple-page">
      <div className="ple-head">
        <Link className="back" to="/clip-room">&larr; Clip Room</Link>
        <h1 title="Click to rename" onClick={rename}>{playlist.name}</h1>
        {playlist.visibility === 'team' && <span className="ple-badge">TEAM</span>}
        <span className="ple-meta">{meta}</span>
        <div className="ple-spacer" />
        {playlist.source === 'rule' && playlist.rule && (
          <button className="ple-btn" disabled={refreshing}
                  onClick={() => void refreshSmartReel()}>
            {refreshing ? 'Refreshing…' : 'Refresh matches'}
          </button>
        )}
        {playlist.is_owner && (
          <>
            <button className="ple-btn" onClick={() => void toggleVisibility()}>
              {playlist.visibility === 'team' ? 'Make private' : 'Share with team'}
            </button>
            <button className="ple-btn danger" onClick={() => void deletePlaylist()}>
              Delete
            </button>
          </>
        )}
        {ex.status === 'ready' && ex.url && (
          <a className="ple-btn" href={ex.url} target="_blank" rel="noreferrer">
            &#8595; Download MP4
          </a>
        )}
        <button className="ple-btn" disabled={ex.status === 'running'}
                onClick={() => void exportReel()}>
          {ex.status === 'running' ? 'Exporting…'
            : ex.status === 'ready' ? 'Re-export'
              : ex.status === 'failed' ? 'Export failed - retry' : 'Export MP4'}
        </button>
        <Link className="ple-btn primary" to={`/playlists/${playlistId}/play`}>
          &#9654; Play reel
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="ple-empty">
          No clips yet. Add moments from any game summary page with the
          playlist button on a moment card.
        </div>
      ) : (
        <div className="ple-list">
          {items.map((it, i) => {
            const score = it.score != null ? Math.round(it.score * 100) : null
            const cls = score == null ? '' : score >= 70 ? '' : score >= 50 ? ' mid' : ' low'
            return (
              <div key={it.id}
                   className={`ple-item${dragOver === it.id ? ' drag-over' : ''}`}
                   draggable
                   onDragStart={() => { dragId.current = it.id }}
                   onDragOver={(e) => { e.preventDefault(); setDragOver(it.id) }}
                   onDragLeave={() => setDragOver(null)}
                   onDrop={(e) => { e.preventDefault(); drop(it.id) }}>
                <span className="ple-grip">&#10495;</span>
                <div className="ple-move">
                  <button disabled={i === 0} onClick={() => moveItem(it.id, -1)}>&#9650;</button>
                  <button disabled={i === items.length - 1} onClick={() => moveItem(it.id, 1)}>&#9660;</button>
                </div>
                <span className="ple-idx">{i + 1}</span>
                <button className="ple-mini"
                        onClick={() => void navigate(`/playlists/${playlistId}/play?start=${i}`)}>
                  &#9654;
                </button>
                <div className="ple-item-main">
                  <div className="ple-name">{titleFor(it)}</div>
                  <div className="ple-imeta">
                    {it.game_code} &middot; {clockFor(it, games[it.game_code])}
                    {it.clip_url ? ' · pre-cut clip' : ''}
                  </div>
                  <button className="ple-note-btn"
                          onClick={() => {
                            if (noteFor === it.id) { setNoteFor(null); return }
                            setNoteFor(it.id)
                            setNoteDraft(it.note ?? '')
                          }}>
                    {it.note ? `\u201C${it.note}\u201D` : '+ note'}
                  </button>
                  {noteFor === it.id && (
                    <input className="ple-note-input" autoFocus
                           value={noteDraft}
                           placeholder="Note shown in the player…"
                           onChange={(e) => setNoteDraft(e.target.value)}
                           onKeyDown={(e) => {
                             if (e.key === 'Enter') void saveNote(it.id)
                             if (e.key === 'Escape') setNoteFor(null)
                           }} />
                  )}
                </div>
                {score != null && <div className={`ple-score${cls}`}>{score}</div>}
                <button className="ple-x" onClick={() => void removeItem(it.id)}>&#10005;</button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
